/**
 * Marketing actions, split out of AppContext.
 *
 * The provider was already carrying settings, clients, the pipeline, alerts
 * and AI; adding campaigns, audiences, templates, journeys and the send queue
 * inline would have made it unreadable. This hook owns that slice and is
 * spread into the same context value, so screens still just call `useApp()`.
 *
 * It takes what it needs rather than reaching for it, which keeps the
 * dependency direction obvious and lets the whole thing be exercised without
 * mounting the provider.
 */
import { useCallback, useMemo } from 'react';

import { uid, digitsOf } from '../lib/format.js';
import { formatDate, parseDate } from '../lib/dates.js';
import { planJourneyRun, storableBody } from '../domain/marketing.js';
import { outgoing, presentation } from '../domain/outgoing.js';
import api from '../lib/api.js';
import { isOffline } from '../lib/storage.js';
import { buildMarketingSeed } from '../constants/marketing.js';
import { pushDesktop } from '../domain/alerts.js';

export function useMarketingActions({
  S, setS, rows, crm, setCrm, mkt, setMkt, replies, events, waMessages, pushMessages, patchClient, markCrmDirty, toast
}) {
  /** Everything the marketing rules need, in the shape they expect. */
  const mktCtx = useMemo(
    () => ({ S, rows, crm, mkt, replies: replies || [], events: events || [], waMessages: waMessages || [], pushMessages: pushMessages || [] }),
    [S, rows, crm, mkt, replies, events, waMessages, pushMessages]
  );

  const stamp = useCallback(() => formatDate(new Date(), false, S.dateFmt), [S.dateFmt]);

  /* ------------------------------------------------------------ records */

  /** Generic upsert into one marketing collection. Returns the id written. */
  const saveMarketingRecord = useCallback((collection, id, data, prefix) => {
    const recordId = id || uid(prefix);
    setMkt(prev => ({
      ...prev,
      [collection]: id
        ? prev[collection].map(x => (x.id === id ? { ...x, ...data } : x))
        : [...prev[collection], { ...data, id: recordId }]
    }));
    return recordId;
  }, [setMkt]);

  const removeMarketingRecord = useCallback((collection, id) => {
    setMkt(prev => ({
      ...prev,
      [collection]: prev[collection].filter(x => x.id !== id),
      // Deleting a journey takes its queued messages with it.
      queue: collection === 'journeys' ? prev.queue.filter(q => q.jid !== id) : prev.queue
    }));
  }, [setMkt]);

  /**
   * Drop the standard set of audiences or templates into an empty workspace.
   *
   * Connected to the API a new org starts with nothing at all, which leaves the
   * campaign wizard with no segment to pick and no copy to start from. These are
   * the definitions the offline sample uses, given fresh ids so they belong to
   * this org, and matched on name so pressing it twice adds nothing.
   */
  const addStarterSet = useCallback(collection => {
    const seed = buildMarketingSeed(d => formatDate(d, false, S.dateFmt));
    const taken = new Set((mkt[collection] || []).map(x => String(x.name).toLowerCase()));
    const fresh = (seed[collection] || [])
      .filter(x => !taken.has(String(x.name).toLowerCase()))
      .map(x => ({ ...x, id: uid(collection === 'audiences' ? 'g' : 't') }));

    if (!fresh.length) { toast('Those are already here'); return 0; }
    setMkt(prev => ({ ...prev, [collection]: [...prev[collection], ...fresh] }));
    toast(`Added ${fresh.length} ${collection === 'audiences' ? 'audiences' : 'templates'}`);
    return fresh.length;
  }, [S.dateFmt, mkt, setMkt, toast]);

  const saveCampaign = useCallback((id, data) => {
    /*
     * Normalised on the way in, not on the way out of the editor. Every path
     * that writes a campaign comes through here — the wizard, a duplicate, a
     * template — and a body that reached storage with a data: URL in it is
     * half a megabyte that no mail client will render.
     */
    const clean = data.body === undefined ? data : { ...data, body: storableBody(data.body) };

    const written = saveMarketingRecord(
      'campaigns', id,
      // `created` is what the screens print; `createdAt` is what they sort on,
      // because a display date has no time and ties on the day it was made.
      id ? clean : { ...clean, created: stamp(), createdAt: new Date().toISOString(), sends: [] },
      'k');
    toast(id ? 'Campaign saved' : 'Campaign created');
    return written;
  }, [saveMarketingRecord, stamp, toast]);

  const saveAudience = useCallback((id, data) => {
    saveMarketingRecord('audiences', id, data, 'g');
    toast('Audience saved');
  }, [saveMarketingRecord, toast]);

  const saveTemplate = useCallback((id, data) => {
    saveMarketingRecord('templates', id, data, 't');
    toast('Template saved');
  }, [saveMarketingRecord, toast]);

  /**
   * Marketing's own address book, kept apart from the sales contact list.
   *
   * Everything here is a name and an address to reach — no owner, no pipeline,
   * no deals — so it lives in the marketing document rather than in `crm`. The
   * campaign wizard reads only this list, which is why importing a bought list
   * for one send no longer floods the Sales screens with rows nobody is
   * working.
   */
  const saveMktContact = useCallback((id, data) => {
    const written = saveMarketingRecord(
      'contacts', id,
      id ? data : { ...data, created: stamp(), createdAt: new Date().toISOString() },
      'mc');
    toast(id ? 'Contact saved' : 'Contact added');
    return written;
  }, [saveMarketingRecord, stamp, toast]);

  /**
   * Add several at once, from a CSV or a paste.
   *
   * Rows already on the list are matched on email, then on phone, and updated
   * rather than duplicated: importing an updated copy of the same list is the
   * ordinary case, and it should not double every address on it.
   */
  const importMktContacts = useCallback(rows => {
    const incoming = (rows || [])
      .map(r => ({ ...r, name: String(r.name || '').trim() }))
      .filter(r => r.name);
    if (!incoming.length) return { added: 0, updated: 0, ids: [] };

    const at = stamp();
    const ids = [];
    let added = 0;
    let updated = 0;

    setMkt(prev => {
      const list = [...(prev.contacts || [])];
      const keyOf = c => String(c.email || '').trim().toLowerCase() || digitsOf(c.phone);

      incoming.forEach(r => {
        const key = keyOf(r);
        const found = key ? list.findIndex(c => keyOf(c) === key) : -1;

        if (found >= 0) {
          list[found] = { ...list[found], ...r, id: list[found].id };
          ids.push(list[found].id);
          updated++;
          return;
        }
        const record = {
          status: 'Subscribed', source: 'Import', company: '', notes: '',
          ...r, id: uid('mc'), created: at, createdAt: new Date().toISOString()
        };
        list.push(record);
        ids.push(record.id);
        added++;
      });

      return { ...prev, contacts: list };
    });

    return { added, updated, ids };
  }, [setMkt, stamp]);

  const saveJourney = useCallback((id, data) => {
    saveMarketingRecord(
      'journeys', id, id ? data : { ...data, enrolled: {}, created: stamp() }, 'j');
    toast('Journey saved');
  }, [saveMarketingRecord, stamp, toast]);

  const toggleJourney = useCallback(id => {
    let label = '';
    setMkt(prev => ({
      ...prev,
      journeys: prev.journeys.map(j => {
        if (j.id !== id) return j;
        label = `${j.name}${j.enabled ? ' is paused' : ' is running'}`;
        return { ...j, enabled: !j.enabled };
      })
    }));
    if (label) toast(label);
  }, [setMkt, toast]);

  /* -------------------------------------------------------------- touch */

  /**
   * Log a marketing touch as a completed activity, so the pipeline's "last
   * touch" and the ledger's "last contacted" both reflect it. Without this a
   * campaign would send and leave no trace anywhere else in the workspace —
   * and the reply/convert figures on the Analytics tab read exactly these
   * fields, so they would stay at zero forever.
   */
  const logMarketingTouch = useCallback((rid, channel, subject, source) => {
    const client = rows.find(x => x.id === rid);
    const contact = crm.contacts.find(x => x.id === rid);
    const type = channel === 'whatsapp' ? 'WhatsApp'
      : channel === 'email' ? 'Email'
        : channel === 'sms' ? 'SMS' : 'Note';
    const at = stamp();

    const entry = {
      id: uid('ac'),
      subject: String(subject || source || 'Marketing message').slice(0, 120),
      type,
      who: (client && client.handler) || (contact && contact.owner) || S.senderName || '',
      dealId: contact
        ? ((crm.deals.find(d => d.contact === contact.id && d.stage !== 'Won' && d.stage !== 'Lost') || {}).id || '')
        : '',
      due: at,
      done: true,
      notes: source || 'Marketing'
    };

    markCrmDirty('acts', entry.id);
    setCrm(prev => ({ ...prev, acts: [...prev.acts, entry] }));
    if (client) patchClient(rid, { lastContacted: at });
  }, [S.senderName, rows, crm, setCrm, patchClient, markCrmDirty, stamp]);

  /* -------------------------------------------------------------- sends */

  /**
   * Record a send or a skip against a campaign. Once everyone in the audience
   * has been handled the campaign closes itself out as Sent.
   */
  const markSend = useCallback((campaignId, rid, status, total) => {
    const at = stamp();
    setMkt(prev => ({
      ...prev,
      campaigns: prev.campaigns.map(c => {
        if (c.id !== campaignId) return c;
        const sends = [...(c.sends || []).filter(s => s.rid !== rid), { rid, status, at }];
        const next = { ...c, sends, sentAt: c.sentAt || at };
        if (status === 'sent' && c.status === 'Draft') next.status = 'Sending';
        if (total && sends.length >= total) next.status = 'Sent';
        return next;
      })
    }));
  }, [setMkt, stamp]);

  /**
   * Write what a bulk send actually did, per recipient.
   *
   * `markSend` records one person at a time and only ever as sent or skipped,
   * which was fine for a hand-off but throws away everything a real send
   * knows: which ones the server refused, why, and which mailbox carried the
   * rest. A send log that cannot say why somebody did not get it is not a log.
   */
  /**
   * Hand a scheduled campaign to the server, finished.
   *
   * Everything the server cannot work out for itself is worked out here and
   * sent whole: who it reaches, and what each of them receives. It follows
   * that the audience is settled now rather than at the hour it goes out —
   * which is what "send this to these people on Friday" means, and what the
   * screen says.
   */
  const queueSend = useCallback(async campaign => {
    if (isOffline()) throw new Error('offline — nothing can be scheduled');

    /* The schedule is kept in the workspace's own date format; the server
       wants an instant. A schedule it cannot read is a campaign that silently
       never goes out, so this refuses rather than guesses. */
    const when = parseDate(campaign.schedule);
    if (!when) throw new Error('that schedule is not a time I can read');

    const { messages, notes } = outgoing(campaign, mktCtx, S);
    if (!messages.length) {
      throw new Error(notes.length
        ? `nobody is reachable (${notes.join(', ')})`
        : 'nobody is reachable');
    }

    const out = await api.marketing.schedule({
      campaignId: campaign.id,
      channel: campaign.channel,
      sendAt: when.toISOString(),
      ...presentation(campaign, S),
      messages: messages.map(m => ({
        rid: m.rid, to: m.to, subject: m.subject, text: m.text, html: m.html
      }))
    });

    toast(`${out.queued} message${out.queued === 1 ? '' : 's'} will go out on their own`);
    return out;
  }, [mktCtx, S, toast]);

  /** Take it back out. Harmless when there was nothing waiting. */
  const unqueue = useCallback(campaignId => {
    if (isOffline()) return;
    api.marketing.unschedule(campaignId).catch(() => {});
  }, []);

  /* Mark a campaign sent on the spot - push and only push, which goes out in
     one call rather than through the send console. */
  const markCampaignSent = useCallback(campaignId => {
    const at = stamp();
    setMkt(prev => ({
      ...prev,
      campaigns: prev.campaigns.map(c => (c.id === campaignId
        ? { ...c, status: 'Sent', sentAt: c.sentAt || at }
        : c))
    }));
  }, [setMkt, stamp]);

  /**
   * Send a push campaign now.
   *
   * A topic send is one call Firebase fans out, so there is no console and no
   * per-recipient loop: the notification goes up whole and the campaign is
   * marked sent. Its record lives in push_messages on the server, which the
   * detail view reads.
   */
  const sendPushNow = useCallback(async campaign => {
    if (isOffline()) throw new Error('offline — push needs the server');
    const source = campaign.pushSource || '';
    const tokens = Array.isArray(campaign.pushTokens) ? campaign.pushTokens : [];
    const out = await api.push.send({
      campaignId: campaign.id,
      title: campaign.pushTitle,
      body: campaign.pushBody,
      image: campaign.pushImage,
      link: campaign.pushLink,
      source,
      app: campaign.pushApp || '',
      topic: (source === 'app' || tokens.length) ? '' : campaign.pushTopic,
      tokens
    });
    markCampaignSent(campaign.id);
    if (out.failed && !out.sent) toast('None went — check the send log');
    else if (out.failed) toast(`Sent to ${out.sent}, ${out.failed} device(s) refused`);
    else if (tokens.length) toast(`Test push sent to ${out.sent} device(s)`);
    else if (source === 'app') toast(`Push sent to ${out.sent} app user(s)`);
    else toast('Push sent');
    return out;
  }, [markCampaignSent, toast]);

  /** Put a push campaign in the queue for its hour. */
  const schedulePush = useCallback(async campaign => {
    if (isOffline()) throw new Error('offline — nothing can be scheduled');
    const when = parseDate(campaign.schedule);
    if (!when) throw new Error('that schedule is not a time I can read');

    const out = await api.marketing.schedule({
      campaignId: campaign.id,
      channel: 'push',
      sendAt: when.toISOString(),
      // The whole notification rides in the presentation; the queue worker
      // reads it back and sends once.
      title: campaign.pushTitle,
      pushBody: campaign.pushBody,
      pushImage: campaign.pushImage,
      pushLink: campaign.pushLink,
      pushTopic: campaign.pushTopic,
      pushSource: campaign.pushSource || '',
      pushApp: campaign.pushApp || '',
      pushTokens: Array.isArray(campaign.pushTokens) ? campaign.pushTokens : [],
      messages: [{ rid: 'push', to: campaign.pushSource === 'app' ? 'app-users' : (campaign.pushTopic || 'topic') }]
    });
    toast('Push will go out on its own');
    return out;
  }, [toast]);

  /**
   * Bring the send log up to date with what went out while nobody was here.
   *
   * The log lives in the marketing document, which this browser replaces
   * wholesale every time it saves — so the scheduler cannot write there, and
   * this is where the two meet. Only rows the log has not already got are
   * applied, so it is safe to run on every load.
   */
  const syncQueue = useCallback(async () => {
    if (isOffline()) return;

    let queued = [];
    try {
      queued = (await api.marketing.queue()).queue || [];
    } catch {
      return;
    }

    const settled = queued.filter(r => r.status === 'sent' || r.status === 'failed');
    if (!settled.length) return;

    const byCampaign = new Map();
    settled.forEach(r => {
      if (!byCampaign.has(r.campaign_id)) byCampaign.set(r.campaign_id, []);
      byCampaign.get(r.campaign_id).push(r);
    });

    setMkt(prev => {
      let touched = false;

      const campaigns = prev.campaigns.map(c => {
        const done = byCampaign.get(c.id);
        if (!done) return c;

        const already = new Map((c.sends || []).map(x => [x.rid, x]));
        const fresh = done.filter(r => {
          const had = already.get(r.rid);
          return !had || (had.status !== 'sent' && r.status === 'sent');
        });
        if (!fresh.length) return c;

        touched = true;

        const sends = [
          ...(c.sends || []).filter(x => !fresh.some(r => r.rid === x.rid)),
          ...fresh.map(r => ({
            rid: r.rid,
            status: r.status === 'sent' ? 'sent' : 'failed',
            at: formatDate(new Date(r.sent_at || r.send_at), true, S.dateFmt),
            sentAt: new Date(r.sent_at || r.send_at).toISOString(),
            error: r.status === 'sent' ? undefined : String(r.error || 'refused'),
            via: r.via || undefined
          }))
        ];

        /* Still waiting on somebody means it is still going out; nothing left
           in the queue for it means it has gone. */
        const waiting = queued.some(r => r.campaign_id === c.id && r.status === 'queued');

        return {
          ...c,
          sends,
          sentAt: c.sentAt || formatDate(new Date(), false, S.dateFmt),
          status: waiting ? 'Sending' : 'Sent'
        };
      });

      return touched ? { ...prev, campaigns } : prev;
    });
  }, [setMkt, S.dateFmt]);

  const recordSends = useCallback((campaignId, entries, total) => {
    const at = stamp();
    const when = new Date().toISOString();

    setMkt(prev => ({
      ...prev,
      campaigns: prev.campaigns.map(c => {
        if (c.id !== campaignId) return c;

        const touched = new Set(entries.map(e => e.rid));
        const sends = [
          ...(c.sends || []).filter(x => !touched.has(x.rid)),
          ...entries.map(e => ({
            rid: e.rid,
            status: e.ok ? 'sent' : 'failed',
            at,
            sentAt: when,
            error: e.ok ? undefined : String(e.error || 'refused'),
            via: e.via || undefined,
            /*
             * The Message-ID this copy went out with. A reply quotes it back,
             * which is how an answer is matched to the campaign that earned
             * it rather than to every campaign that ever wrote to the same
             * person.
             */
            mid: e.mid || undefined
          }))
        ];

        const accepted = sends.filter(x => x.status === 'sent').length;

        /*
         * Done is done. `markSend` closed a campaign out once everybody had
         * been handled and this did not, so a send that finished in one go sat
         * on "Sending" for ever — the screen saying it was still working while
         * every message had already gone.
         */
        const finished = total && sends.length >= total;
        const status = finished ? 'Sent'
          : accepted && c.status === 'Draft' ? 'Sending'
            : c.status;

        return { ...c, sends, sentAt: c.sentAt || at, status };
      })
    }));
  }, [setMkt, stamp]);

  const markAllSent = useCallback((campaignId, ids) => {
    const at = stamp();
    setMkt(prev => ({
      ...prev,
      campaigns: prev.campaigns.map(c => (c.id === campaignId
        ? {
          ...c,
          sends: [
            ...(c.sends || []).filter(s => !ids.includes(s.rid)),
            ...ids.map(rid => ({ rid, status: 'sent', at }))
          ],
          status: 'Sent',
          sentAt: c.sentAt || at
        }
        : c))
    }));

    /*
     * Both routes to sent must leave the same trace. Marking recipients one by
     * one logs a touch each; without this the bulk button would flip the
     * campaign to Sent while the ledger and the pipeline last-touch figures
     * carried on as though nobody had been contacted.
     */
    const campaign = mkt.campaigns.find(c => c.id === campaignId);
    if (campaign) {
      ids.forEach(rid => logMarketingTouch(
        rid, campaign.channel, campaign.subject || campaign.name, 'Campaign: ' + campaign.name));
    }
  }, [setMkt, stamp, mkt.campaigns, logMarketingTouch]);

  /* ----------------------------------------------------------- opt-outs */

  const addOptOut = useCallback(value => {
    const key = String(value || '').trim().toLowerCase();
    if (!key) return;
    setMkt(prev => (prev.optouts.includes(key)
      ? prev
      : { ...prev, optouts: [...prev.optouts, key] }));
    toast('Added to the opt-out list');
  }, [setMkt, toast]);

  const removeOptOut = useCallback(index => {
    setMkt(prev => ({ ...prev, optouts: prev.optouts.filter((_, i) => i !== index) }));
  }, [setMkt]);

  /** Channel configuration lives in settings, not in the marketing store. */
  const setChannelSetting = useCallback((key, value) => {
    setS(prev => ({ ...prev, mkt: { ...(prev.mkt || {}), [key]: value } }));
  }, [setS]);


  /* ----------------------------------------------------------- journeys */

  /** Advance every running journey and apply the plan it returns. */
  const runJourneys = useCallback(({ announce = false } = {}) => {
    const plan = planJourneyRun(mktCtx);

    setMkt(prev => ({
      ...prev,
      journeys: plan.journeys,
      queue: [...prev.queue, ...plan.queued.map(q => ({ ...q, id: uid('q') }))]
    }));

    if (plan.tasks.length) {
      const created = plan.tasks.map(t => ({ ...t, id: uid('ac') }));
      created.forEach(t => markCrmDirty('acts', t.id));
      setCrm(prev => ({ ...prev, acts: [...prev.acts, ...created] }));
    }

    if (announce) {
      if (plan.notify.length) pushDesktop(S, plan.notify);
      if (plan.queued.length) {
        toast(`${plan.queued.length} journey message${plan.queued.length > 1 ? 's' : ''} ready to send`);
      }
    }
    return plan;
  }, [mktCtx, S, setMkt, setCrm, markCrmDirty, toast]);

  const setQueueStatus = useCallback((id, status) => {
    const at = stamp();
    setMkt(prev => ({
      ...prev,
      queue: prev.queue.map(q => (q.id === id ? { ...q, status, at } : q))
    }));
  }, [setMkt, stamp]);

  const clearQueue = useCallback(() => {
    setMkt(prev => ({
      ...prev,
      queue: prev.queue.map(q => (q.status === 'pending' ? { ...q, status: 'discarded' } : q))
    }));
  }, [setMkt]);

  const markQueueAllSent = useCallback(() => {
    const at = stamp();
    setMkt(prev => ({
      ...prev,
      queue: prev.queue.map(q => (q.status === 'pending' ? { ...q, status: 'sent', at } : q))
    }));
  }, [setMkt, stamp]);

  return useMemo(() => ({
    mktCtx,
    saveCampaign, saveAudience, saveTemplate, saveJourney, addStarterSet,
    saveMktContact, importMktContacts,
    removeMarketingRecord, toggleJourney,
    markSend, markAllSent, recordSends, queueSend, unqueue, syncQueue,
    sendPushNow, schedulePush,
    addOptOut, removeOptOut, setChannelSetting,
    logMarketingTouch,
    runJourneys, setQueueStatus, clearQueue, markQueueAllSent
  }), [
    mktCtx, saveCampaign, saveAudience, saveTemplate, saveJourney, addStarterSet,
    saveMktContact, importMktContacts,
    removeMarketingRecord, toggleJourney, markSend, markAllSent, recordSends,
    sendPushNow, schedulePush,
    queueSend, unqueue, syncQueue,
    addOptOut, removeOptOut, setChannelSetting, logMarketingTouch,
    runJourneys, setQueueStatus, clearQueue, markQueueAllSent
  ]);
}
